import { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { touchLastActive } from "@/lib/activityStatus";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, fullName: string, phone?: string, smsAlertsConsent?: boolean, smsMarketingConsent?: boolean) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user && (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "INITIAL_SESSION")) {
          // Fire-and-forget activity ping
          setTimeout(() => { touchLastActive(); }, 0);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, phone?: string, smsAlertsConsent?: boolean) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone: phone,
        },
      },
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    // Update profile with phone number and SMS consent after signup
    if (data.user) {
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          phone,
          notification_preferences: {
            booking_requests: true,
            booking_updates: true,
            messages: true,
            reviews: true,
            reminders: true,
            marketing: false,
            sms_enabled: smsAlertsConsent ?? false,
            sms_alerts_consent: smsAlertsConsent ?? false,
            sms_marketing_consent: false,
          },
        })
        .eq("id", data.user.id);
      
      if (profileError) {
        console.error("Error updating profile with phone:", profileError);
      }
    }

    // Supabase returns a user with no identities when the email is already registered
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      return { error: { message: "An account with this email already exists. Please log in instead." } };
    }

    return { error: null, needsConfirmation: !data.session } as any;
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success("Welcome back!");
    // Honor a same-origin ?next= return URL (path + query preserved)
    const next = new URLSearchParams(window.location.search).get("next") ?? "";
    navigate(next.startsWith("/") && !next.startsWith("//") ? next : "/", { replace: true });
    return { error: null };
  };

  const signOut = async () => {
    // Try global sign out, then fall back to local so a stale/missing server
    // session never blocks the user from logging out on their device.
    let { error } = await supabase.auth.signOut();
    if (error) {
      console.warn("Global signOut failed, falling back to local:", error.message);
      const localResult = await supabase.auth.signOut({ scope: "local" });
      error = localResult.error;
    }
    // Always clear local state and navigate, even if Supabase reported an error
    setSession(null);
    setUser(null);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Logged out successfully");
    }
    navigate("/");
  };


  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    try {
      const res = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (res.error) {
        toast.error("Something went wrong. Please try again.");
        return { error: res.error };
      }

      toast.success("Password reset email sent! Check your inbox.");
      return { error: null };
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      return { error: err };
    }
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    toast.success("Password updated successfully!");
    navigate("/");
    return { error: null };
  };

  return (
    <AuthContext.Provider value={{ user, session, signUp, signIn, signOut, resetPassword, updatePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
